import React, { memo } from 'react';
import { createPortal } from 'react-dom';

interface CopyDragOverlayProps {
  visible: boolean;
  overlayRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * 方案 B 拖拽浮层容器（文档 5.6）：
 * - createPortal 到 document.body，脱离 React Flow transform 缩放上下文
 * - position: fixed，由 useCopyDrag 通过 ref 直接改 style.transform 跟随鼠标（零重渲染）
 * - pointerEvents: none，避免拦截后续 mousemove/mouseup
 * - 内容由 useCopyDrag 在 mousedown 时克隆选中节点的真实 DOM 填充，视觉与原节点完全一致
 */
const CopyDragOverlayInner: React.FC<CopyDragOverlayProps> = ({ visible, overlayRef }) => {
  if (!visible) return null;

  return createPortal(
    <div
      ref={overlayRef}
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        // 初始 transform，useCopyDrag 的 mousemove 会直接覆盖此 style
        // transformOrigin 需为 0 0，让 scale 基于左上角缩放（与 React Flow viewport 一致）
        transform: 'translate(-100px, -100px) scale(1)',
        transformOrigin: '0 0',
        pointerEvents: 'none',
        zIndex: 9999,
        opacity: 0.92,
      }}
    />,
    document.body,
  );
};

export const CopyDragOverlay = memo(CopyDragOverlayInner, (prev, next) => {
  // 仅在 visible 切换时重渲染；位置与内容由 ref 直接操作 DOM，不触发 React 渲染
  return prev.visible === next.visible && prev.overlayRef === next.overlayRef;
});
